import React, { useState } from 'react';
import { styles } from './styles';
import ReflectionButton from '../reflectionButton/reflectionButton';
import { RIPPLE_ON_DARK } from '../../globalStyles';

interface SandwichContainerProps {
  index?: string;
  title: string;
  description?: string;
  // Cap on the expanded panel. The default suits a product blurb; a long document (the legal
  // page) passes its own. It has to be a fixed value rather than 'none' because max-height is
  // what the open/close transition animates, and 'none' is not an animatable length.
  maxHeight?: string;
  // Mount already expanded. Used by /legal so a `#privacy` link lands on a readable document
  // rather than a shut row. Read once at mount — this is the row's INITIAL state, not a
  // controlled one, so the badge keeps full ownership of open/closed afterwards.
  startOpen?: boolean;
  children?: React.ReactNode;
  isLast?: boolean;
}

const SandwichContainer: React.FC<SandwichContainerProps> = ({
  index,
  title,
  description,
  maxHeight = '500px',
  startOpen = false,
  children,
  isLast = false,
}) => {
  const [open, setOpen] = useState(startOpen);
  const hasChildren = Boolean(children);

  const badgeStyle = hasChildren
    ? open
      ? { ...styles.badge, ...styles.badgeLess }
      : { ...styles.badge, ...styles.badgeMore }
    : { ...styles.badge, ...styles.badgeComingSoon };

  const badgeLabel = hasChildren ? (open ? 'Less' : 'More') : 'Coming soon';

  return (
    <div style={isLast ? styles.rowLast : styles.row}>
      <div style={styles.header}>
        {index && <span style={styles.index}>{index}</span>}
        <div style={styles.info}>
          <p style={styles.title}>{title}</p>
          {description && <p style={styles.description}>{description}</p>}
        </div>
        <ReflectionButton
          style={badgeStyle}
          highlightColor={RIPPLE_ON_DARK}
          onClick={() => hasChildren && setOpen((prev) => !prev)}
          disabled={!hasChildren}
        >
          {badgeLabel}
        </ReflectionButton>
      </div>

      {hasChildren && (
        <div
          style={{
            ...styles.children,
            maxHeight: open ? maxHeight : '0px',
            opacity: open ? 1 : 0,
          }}
        >
          <div className="sandwich-inner" style={styles.childrenInner}>{children}</div>
        </div>
      )}
    </div>
  );
};

export default SandwichContainer;
